import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { AppSidebar } from '@/components/navigation/AppSidebar';
import { AppSidebarTrigger } from '@/components/navigation/AppSidebarTrigger';
import { useAppNavigation } from '@/components/navigation/app-navigation';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { DemoProfileBanner } from './layout/DemoProfileBanner';
import { PageHeader } from './layout/PageHeader';
import { useDocumentTitle } from './layout/hooks/use-document-title';
import { PianoIdentity } from './layout/PianoIdentity';
import { PrintMargins } from './print/PrintMargins';
export interface MainLayoutProps {
  children: React.ReactNode;
  /** Replaces the default content container, for pages that run full-bleed. */
  contentClassName?: string;
  /** What sits on the right of the thin header bar. */
  headerRight?: React.ReactNode;
  /** Hides the demo strip on pages that are not about the instrument. */
  shouldDisplayDemoBanner?: boolean;
  /** `bare` drops the header bar and the content container (the home page). */
  variant?: 'app' | 'bare';
}
export const MainLayout = t0 => {
  const $ = _c(28);
  const {
    children,
    contentClassName,
    headerRight,
    shouldDisplayDemoBanner: t1,
    variant: t2
  } = t0;
  const shouldDisplayDemoBanner = t1 === undefined ? true : t1;
  const variant = t2 === undefined ? "app" : t2;
  const steps = useAppNavigation();
  useDocumentTitle(steps);
  const {
    t
  } = useTranslation();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      "--sidebar-width": "var(--ptl-sidebar-width)"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== t) {
    t4 = t("Skip to content");
    $[1] = t;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = <a href="#page-content" className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-3 focus-visible:left-3 focus-visible:z-50 focus-visible:rounded-md focus-visible:bg-card focus-visible:px-4 focus-visible:py-2.5 focus-visible:text-sm focus-visible:font-semibold focus-visible:shadow-md">{t4}</a>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== steps) {
    t6 = <AppSidebar steps={steps} />;
    $[5] = steps;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <PrintMargins />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== headerRight || $[9] !== steps || $[10] !== variant) {
    t8 = variant === "app" ? <PageHeader steps={steps} right={headerRight ?? <PianoIdentity />} /> : <AppSidebarTrigger className="mt-4 ml-4" />;
    $[8] = headerRight;
    $[9] = steps;
    $[10] = variant;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== shouldDisplayDemoBanner) {
    t9 = shouldDisplayDemoBanner ? <DemoProfileBanner /> : null;
    $[12] = shouldDisplayDemoBanner;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = variant === "app" && "flex w-full max-w-[1720px] flex-col gap-6 px-10 pt-8 pb-14 max-md:px-4 max-md:pt-6 max-md:pb-10";
  let t11;
  if ($[14] !== contentClassName || $[15] !== t10) {
    t11 = cn(t10, contentClassName);
    $[14] = contentClassName;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== children || $[18] !== t11) {
    t12 = <div id="page-content" tabIndex={-1} data-slot="page-content" className={t11}>{children}</div>;
    $[17] = children;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t8 || $[22] !== t9) {
    t13 = <SidebarInset className="min-w-0">{t8}{t9}{t12}</SidebarInset>;
    $[20] = t12;
    $[21] = t8;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t5 || $[26] !== t6) {
    t14 = <SidebarProvider style={t3 as React.CSSProperties}>{t5}{t6}{t7}{t13}</SidebarProvider>;
    $[24] = t13;
    $[25] = t5;
    $[26] = t6;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
};